// F-062 : messagerie par logement et par bail (bailleur, locataire, délégué).
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, Input, Loading, Modal, PageHeader, Textarea, fieldError } from "@core/ui";
import { BackLink, LeaseSelect, PropertySelect, docUrl, meLease, orgApi, orgPath, sendFile, useAction } from "./shared";
import { TenantDelegations } from "./delegations";

type Conversation = {
  id: string;
  subject: string;
  lease_id: string | null;
  property_label: string | null;
  last_message_at: string | null;
  message_count: number;
  unread: number;
  last_excerpt: string | null;
  shared_with_tenant: boolean;
};

/** Fil de messages : lecture, réponse et pièces jointes. `uploadPath` absent : pas de pièce jointe. */
export function Thread({ getPath, postPath, canWrite, upload, onPosted }: { getPath: string; postPath: string; canWrite: boolean; upload?: { path: string; fields?: Record<string, string> }; onPosted?: () => void }) {
  const q = useGet<any>(getPath);
  const invalidate = useInvalidate();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<{ id: string; filename: string }[]>([]);
  const act = useAction();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const conv = q.data.conversation as Conversation;
  const messages = q.data.messages as any[];
  return (
    <div className="stack">
      <div className="row-between">
        <div>
          <h2>{conv.subject}</h2>
          <p className="small muted">
            {conv.property_label}
            {conv.shared_with_tenant ? " · échange partagé avec les locataires du bail" : " · échange interne à l’espace"}
          </p>
        </div>
      </div>
      <div className="vie-thread" aria-live="polite">
        {messages.map((m) => (
          <article key={m.id} className={`vie-bubble ${m.mine ? "vie-bubble-mine" : ""}`}>
            <p className="small muted">
              <strong>{m.author_label}</strong> · {dateTime(m.created_at)}
            </p>
            {m.body ? <p className="vie-bubble-body">{m.body}</p> : null}
            {m.attachments?.length ? (
              <ul className="list-plain vie-docs">
                {m.attachments.map((a: any) => (
                  <li key={a.id}>
                    <a href={docUrl(a.id)} target="_blank" rel="noreferrer">
                      {a.title || a.filename}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </article>
        ))}
      </div>
      {canWrite ? (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await act.run(() => post(postPath, { body, attachment_ids: files.map((f) => f.id) }), "Message envoyé.");
            if (ok) {
              setBody("");
              setFiles([]);
              invalidate(getPath, getPath.split("/conversations")[0]);
              onPosted?.();
            }
          }}
        >
          <ErrorBox error={act.error} />
          <Textarea label="Votre message" value={body} error={fieldError(act.error, "body")} onChange={(e) => setBody(e.target.value)} />
          {upload ? (
            <FileDrop
              multiple
              label="Joindre une pièce"
              busy={act.busy}
              onFiles={async (list) => {
                for (const f of list) {
                  const doc = await act.run(() => sendFile(upload.path, f, upload.fields ?? {}));
                  if (doc) setFiles((xs) => [...xs, { id: doc.id, filename: doc.filename }]);
                }
              }}
            />
          ) : null}
          {files.length ? <p className="small">Pièces jointes : {files.map((f) => f.filename).join(", ")}</p> : null}
          <div className="row">
            <Button type="submit" busy={act.busy}>
              Envoyer
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

function ConversationList({ items, selected, onSelect }: { items: Conversation[]; selected?: string; onSelect: (id: string) => void }) {
  if (!items.length) return <Empty title="Aucune conversation">Les échanges avec vos locataires apparaîtront ici.</Empty>;
  return (
    <ul className="vie-list">
      {items.map((c) => (
        <li key={c.id}>
          <button type="button" className="vie-list-item" aria-current={selected === c.id ? "true" : undefined} onClick={() => onSelect(c.id)}>
            <span className="row-between">
              <strong>{c.subject}</strong>
              {c.unread ? <Badge tone="encre">{c.unread} non lu{c.unread > 1 ? "s" : ""}</Badge> : null}
            </span>
            <span className="small muted">
              {c.property_label ?? ""} · {c.last_message_at ? dateTime(c.last_message_at) : "—"}
              {!c.shared_with_tenant ? " · interne" : ""}
            </span>
            {c.last_excerpt ? <span className="small">{c.last_excerpt}</span> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

function NewConversation({ orgId, leaseId, onCreated }: { orgId: string; leaseId?: string; onCreated: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lease_id: leaseId ?? "", property_id: "", subject: "", body: "", internal: false });
  const act = useAction();
  const invalidate = useInvalidate();
  return (
    <>
      <Button onClick={() => setOpen(true)}>Nouvelle conversation</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Nouvelle conversation"
        footer={
          <Button
            busy={act.busy}
            onClick={async () => {
              const payload = form.internal ? { property_id: form.property_id, subject: form.subject, body: form.body } : { lease_id: form.lease_id, subject: form.subject, body: form.body };
              const r = await act.run(() => post<{ id: string }>(orgApi(orgId, "/conversations"), payload), "Conversation créée.");
              if (r) {
                setOpen(false);
                invalidate(orgApi(orgId, "/conversations"));
                onCreated(r.id);
              }
            }}
          >
            Envoyer
          </Button>
        }
      >
        <div className="stack">
          <ErrorBox error={act.error} />
          {!leaseId ? (
            <Checkbox label="Note interne à l’espace (non partagée avec les locataires)" checked={form.internal} onChange={(v) => setForm({ ...form, internal: v })} />
          ) : null}
          {form.internal ? (
            <PropertySelect orgId={orgId} value={form.property_id} onChange={(v) => setForm({ ...form, property_id: v })} />
          ) : !leaseId ? (
            <LeaseSelect orgId={orgId} value={form.lease_id} error={fieldError(act.error, "lease_id")} onChange={(v) => setForm({ ...form, lease_id: v })} />
          ) : null}
          <Input label="Objet" required value={form.subject} error={fieldError(act.error, "subject")} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <Textarea label="Message" value={form.body} error={fieldError(act.error, "body")} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </div>
      </Modal>
    </>
  );
}

/** Messagerie de l'espace : toutes les conversations, recherche, fil sélectionné. */
export function MessagesPage() {
  const org = useOrg();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const list = useGet<{ items: Conversation[]; unread: number }>(orgApi(org.orgId, `/conversations${q ? `?q=${encodeURIComponent(q)}` : ""}`));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Vie du logement"
        title="Messages"
        intro="Chaque conversation est rattachée à un bail ou à un logement. Les locataires ne voient que les échanges de leur propre bail."
        actions={org.can("operate") ? <NewConversation orgId={org.orgId} onCreated={(id) => nav(org.to(`messages/${id}`))} /> : null}
      />
      <Input label="Rechercher dans les échanges" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
      {list.isLoading ? <Loading /> : <ConversationList items={list.data?.items ?? []} onSelect={(id) => nav(org.to(`messages/${id}`))} />}
    </div>
  );
}

export function ThreadPage() {
  const org = useOrg();
  const { conversationId = "" } = useParams();
  const q = useGet<any>(orgApi(org.orgId, `/conversations/${conversationId}`));
  const leaseId = q.data?.conversation?.lease_id as string | null | undefined;
  return (
    <div className="page">
      <BackLink to={org.to("messages")}>Toutes les conversations</BackLink>
      <Card>
        <Thread
          getPath={orgApi(org.orgId, `/conversations/${conversationId}`)}
          postPath={orgApi(org.orgId, `/conversations/${conversationId}/messages`)}
          canWrite={org.can("operate")}
          upload={leaseId ? { path: org.api("/documents"), fields: { lease_id: leaseId, category: "piece_jointe_message" } } : undefined}
        />
      </Card>
    </div>
  );
}

/** Onglet « Messages » de la page du bail. */
export function LeaseMessagesTab({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const [selected, setSelected] = useState<string>("");
  const list = useGet<{ items: Conversation[] }>(orgApi(orgId, `/conversations?lease_id=${leaseId}`));
  const items = list.data?.items ?? [];
  const current = selected || items[0]?.id || "";
  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted">Échanges avec les locataires de ce bail. Un nouveau bail sur le logement démarre avec une messagerie vide.</p>
        <NewConversation orgId={orgId} leaseId={leaseId} onCreated={setSelected} />
      </div>
      {list.isLoading ? (
        <Loading />
      ) : (
        <div className="vie-split">
          <ConversationList items={items} selected={current} onSelect={setSelected} />
          {current ? (
            <Card>
              <Thread
                key={current}
                getPath={orgApi(orgId, `/conversations/${current}`)}
                postPath={orgApi(orgId, `/conversations/${current}/messages`)}
                canWrite
                upload={{ path: `/api/v1/orgs/${orgId}/documents`, fields: { lease_id: leaseId, category: "piece_jointe_message" } }}
              />
            </Card>
          ) : null}
        </div>
      )}
      <p className="small">
        <Link to={orgPath(orgId, "messages")}>Voir toutes les conversations de l’espace</Link>
      </p>
    </div>
  );
}

/** Section « Messages » de la location, côté locataire. */
export function TenantMessagesSection({ leaseId }: { leaseId: string }) {
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState({ subject: "", body: "" });
  const [creating, setCreating] = useState(false);
  const list = useGet<{ items: Conversation[] }>(meLease(leaseId, "/conversations"));
  const act = useAction();
  const invalidate = useInvalidate();
  const items = list.data?.items ?? [];
  const current = selected || items[0]?.id || "";
  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted">Écrivez à votre bailleur. Vos échanges restent attachés à votre location.</p>
        <Button variant="secondaire" onClick={() => setCreating(!creating)}>
          {creating ? "Fermer" : "Nouveau message"}
        </Button>
      </div>
      {creating ? (
        <Card title="Nouveau message">
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              const r = await act.run(() => post<{ id: string }>(meLease(leaseId, "/conversations"), form), "Message envoyé à votre bailleur.");
              if (r) {
                setForm({ subject: "", body: "" });
                setCreating(false);
                setSelected(r.id);
                invalidate(meLease(leaseId, "/conversations"));
              }
            }}
          >
            <ErrorBox error={act.error} />
            <Input label="Objet" required value={form.subject} error={fieldError(act.error, "subject")} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <Textarea label="Message" required value={form.body} error={fieldError(act.error, "body")} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <Button type="submit" busy={act.busy}>
              Envoyer
            </Button>
          </form>
        </Card>
      ) : null}
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : (
        <div className="vie-split">
          <ConversationList items={items} selected={current} onSelect={setSelected} />
          {current ? (
            <Card>
              <Thread
                key={current}
                getPath={meLease(leaseId, `/conversations/${current}`)}
                postPath={meLease(leaseId, `/conversations/${current}/messages`)}
                canWrite
                upload={{ path: meLease(leaseId, "/attachments") }}
              />
            </Card>
          ) : null}
        </div>
      )}
      <TenantDelegations leaseId={leaseId} />
    </div>
  );
}
